import { METHOD, webRequest } from '../../client/HttpClient';
import { API } from '../../client/RouteConstants';
import { IS_FULL_ERROR_CODE } from './constants';
import { buildURL, logAPIErrorToNewrelic, logAddItemDetailsToNR } from '../utils';
import { ACTION, Analytics } from '../../analytics/analytics';
import { APIResult } from '../APIResult';
import {
    LIST_FULL_ERROR_MESSAGE,
    MONETATE_ITEM_ADDED_FROM_PDP_KEY,
    MONETATE_ITEM_ADDED_FROM_PLP_KEY,
    MONETATE_ITEM_ADDED_KEY,
    MONETATE_ITEM_ADDED_FROM_BASKET_KEY,
} from '../../../stores/constants';
import { trackEvent } from '../../monetate/monetate';

export interface AddItemToWishListRequest {
    skuId: string;
    asafProperties?: ASAFProperties;
}

export const addItemToWishList = async (
    wishListId: string,
    skuId: string,
    itemDetails: object,
    productMetadata?: ProductMetadata,
    isAddingFromPLP?: boolean,
    isAddingFromBasket?: boolean,
): Promise<APIResult<WishListItem>> => {
    const url = buildURL(`${API.WISH_LISTS}/${wishListId}?productMode=true&includesImages=true`);
    const addItemRequest: AddItemToWishListRequest = {
        skuId: skuId,
        asafProperties: productMetadata?.asafProperties,
    };

    const request: RequestInit = {
        method: METHOD.POST,
        headers: {
            'Content-Type': 'application/json',
            'x-api-client-id': 'add-to-wishlist-esi',
        },
        body: JSON.stringify(addItemRequest),
        credentials: 'include',
    };

    const res = await webRequest(url, request);

    const result = new APIResult<WishListItem>();
    if (!res) {
        logAPIErrorToNewrelic(`addItemToWishList`, `Empty response.`, {
            ...request,
            url: url,
            errorMessage: result.errorMessage,
            showError: result.showError,
            result: result,
            response: res,
        });
        return result;
    }

    if (res.ok) {
        Analytics.dispatchAdobeAnalytics(ACTION.ADD_TO_WISH_LIST);
        trackEvent(MONETATE_ITEM_ADDED_KEY); // Global event tracked for additions from either of PLP or PDP
        trackEvent(isAddingFromPLP ? MONETATE_ITEM_ADDED_FROM_PLP_KEY : MONETATE_ITEM_ADDED_FROM_PDP_KEY); // Track individual additions from PLP or PDP
        const customEvent = new CustomEvent('ADD_TO_WISHLIST_ESI:SUCCESS', {
            detail: {
                code: 'wishlist success',
            },
        });
        window.dispatchEvent(customEvent);
        isAddingFromBasket && trackEvent(MONETATE_ITEM_ADDED_FROM_BASKET_KEY); // Track individual additions from Basket
        result.success = true;
        result.data = await res.json();
        logAddItemDetailsToNR(itemDetails);
    } else {
        logAPIErrorToNewrelic(`addItemToWishList`, `Response Not OK`, { ...res, request: request, url: url });
    }

    if (res.status === 401) {
        result.showError = false;
        logAPIErrorToNewrelic(`addItemToWishList`, `UNAUTHORISED - 401`, {
            ...res,
            request: request,
            url: url,
            result: result,
            errorMessage: result.errorMessage,
            showError: result.showError,
            response: res,
        });
    }

    if (res.status == 400) {
        const errorResponse: WishListAPIError = await res.json();
        // errorResponse.response will always return an array of 1 item.
        if (errorResponse.response[0].statusCode === IS_FULL_ERROR_CODE) {
            logAPIErrorToNewrelic(`addItemToWishList`, `BAD_REQUEST - 400 [List Full]`, {
                ...res,
                request: request,
                url: url,
                result: result,
                errorMessage: result.errorMessage,
                showError: result.showError,
                response: res,
            });
            result.errorMessage = LIST_FULL_ERROR_MESSAGE;
        }
    }

    return result;
};
